import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import RichText from "@/components/ai/RichText";

const SUGGESTIONS = [
  "Quali pagamenti sono scaduti o in scadenza?",
  "Quali fornitori mancano per questo evento?",
  "Quanti invitati confermati ci sono?",
  "Quali attività sono ancora aperte?",
];

/**
 * Wedding Command Center — barra "Chiedi ad Aurea" (Premium).
 * Pulsante flottante globale → dialog con domanda/risposta sui dati dell'agenzia.
 */
export default function CommandBar() {
  const { user } = useAuth();
  const [aiEnabled, setAiEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [thread, setThread] = useState([]); // [{role:"user"|"assistant", text}]
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/ai/status").then(({ data }) => setAiEnabled(!!data.ai_enabled)).catch(() => setAiEnabled(false));
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread, loading]);

  const isPremium = user?.plan === "premium";
  if (!isPremium || !aiEnabled) return null;

  const currentEventId = () => {
    const m = window.location.pathname.match(/\/events\/([^/]+)/);
    return m ? m[1] : undefined;
  };

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setThread((prev) => [...prev, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/command", { message: q, event_id: currentEventId() });
      setThread((prev) => [...prev, { role: "assistant", text: data.answer || "—" }]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setThread((prev) => [...prev, { role: "assistant", text: "Si è verificato un errore. Riprova." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg px-4 py-3 text-sm hover:bg-primary/90 transition-colors"
        data-testid="command-bar-fab"
      >
        <Sparkles className="w-4 h-4" /> Chiedi ad Aurea
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-xl">
          <DialogHeader>
            <DialogTitle className="serif text-2xl font-normal inline-flex items-center gap-2">
              <Sparkles className="w-5 h-5" /> Chiedi ad Aurea
            </DialogTitle>
          </DialogHeader>

          <div ref={scrollRef} className="max-h-[45vh] overflow-y-auto space-y-3 pr-1" data-testid="command-thread">
            {thread.length === 0 && !loading && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Fammi una domanda sui tuoi eventi, pagamenti, ospiti o fornitori.</p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => ask(s)} className="text-xs border border-border rounded-full px-3 py-1 hover:bg-secondary transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {thread.map((m, i) => (
              <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                <div className={`inline-block rounded-sm px-3 py-2 text-sm max-w-[85%] text-left ${m.role === "user" ? "bg-primary text-primary-foreground whitespace-pre-wrap" : "bg-muted/50"}`}>
                  {m.role === "user" ? m.text : <RichText text={m.text} className="space-y-1" />}
                </div>
              </div>
            ))}
            {loading && (
              <div className="text-left">
                <div className="inline-flex items-center gap-2 rounded-sm px-3 py-2 text-sm bg-muted/50 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Sto cercando nei tuoi dati…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 pt-1">
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Chiedi ad Aurea…" className="rounded-sm" disabled={loading} data-testid="command-input" autoFocus />
            <Button type="submit" disabled={loading || !input.trim()} className="rounded-sm bg-primary text-primary-foreground" data-testid="command-send">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
